import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import { axiosInstance, API } from '@/App';

// Configure PDF.js worker - use CDN for reliable loading
// Match the version with pdfjs-dist in package.json
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const AdminFormMapper = () => {
  const { formId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [fields, setFields] = useState([]);
  const [selectedField, setSelectedField] = useState(null);
  const [showFieldConfig, setShowFieldConfig] = useState(false);
  const [draggedFieldType, setDraggedFieldType] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [aiDetecting, setAiDetecting] = useState(false);
  const [pdfRetryCount, setPdfRetryCount] = useState(0);
  const [pdfKey, setPdfKey] = useState(0); // Key to force re-render of PDF component
  
  // Resize/drag state
  const [isResizing, setIsResizing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [fieldStart, setFieldStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  
  const pdfContainerRef = useRef(null);
  const [pdfDimensions, setPdfDimensions] = useState({ width: 612, height: 792 });

  const fieldTypes = [
    { type: 'text', label: 'Text Input', icon: '📝' },
    { type: 'date', label: 'Date', icon: '📅' },
    { type: 'checkbox', label: 'Checkbox', icon: '☑️' },
    { type: 'signature', label: 'Signature', icon: '✍️' },
    { type: 'number', label: 'Number', icon: '🔢' },
    { type: 'email', label: 'Email', icon: '📧' },
    { type: 'phone', label: 'Phone', icon: '📱' },
    { type: 'ssn', label: 'SSN', icon: '🔒' },
  ];

  useEffect(() => {
    fetchForm();
  }, [formId]);

  const fetchForm = async () => {
    try {
      setLoading(true);
      setPdfError(null);
      const response = await axiosInstance.get(`/admin/custom-forms/${formId}`);
      setForm(response.data);
      setFields(response.data.fields || []);
      if (response.data.fields?.[0]) {
        setPdfDimensions({
          width: response.data.fields[0].pageWidth || 612,
          height: response.data.fields[0].pageHeight || 792
        });
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching form:', error);
      const errorMsg = error.response?.data?.detail || 'Error loading form';
      setLoading(false);
      if (error.response?.status === 404) {
        setPdfError('This form no longer exists or the PDF file is missing.');
        // Use setTimeout to ensure state updates before navigation
        setTimeout(() => {
          if (window.confirm('This form no longer exists or the PDF file is missing. Go back to forms list?')) {
            navigate('/admin/custom-forms', { replace: true });
          }
        }, 100);
      } else {
        setPdfError(errorMsg);
        alert(errorMsg);
      }
    }
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPdfError(null);
  };

  const onPageLoadSuccess = (page) => {
    const { width, height } = page;
    console.log(`PDF page rendered at: ${width} x ${height}`);
    setPdfDimensions({ width, height });
  };

  const onDocumentLoadError = (error) => {
    console.error('PDF Load Error:', error, 'Retry count:', pdfRetryCount);
    
    // Auto-retry up to 3 times with increasing delay
    if (pdfRetryCount < 3) {
      const delay = (pdfRetryCount + 1) * 1000; // 1s, 2s, 3s
      console.log(`Retrying PDF load in ${delay}ms...`);
      setTimeout(() => {
        setPdfRetryCount(prev => prev + 1);
        setPdfKey(prev => prev + 1); // Force re-render
        setPdfError(null);
      }, delay);
    } else {
      setPdfError('Could not load PDF after multiple attempts. Please refresh the page.');
    }
  };

  // Get mouse position relative to PDF container
  const getMousePosition = useCallback((e) => {
    const container = pdfContainerRef.current;
    if (!container) return { x: 0, y: 0 };
    const rect = container.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / scale,
      y: (e.clientY - rect.top) / scale
    };
  }, [scale]);

  // Handle adding new field from palette
  const handlePaletteDragStart = (e, fieldType) => {
    setDraggedFieldType(fieldType);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (!draggedFieldType) return;

    const pos = getMousePosition(e);
    const newField = {
      fieldId: `field_${Date.now()}`,
      fieldType: draggedFieldType,
      label: `${draggedFieldType.charAt(0).toUpperCase() + draggedFieldType.slice(1)} Field`,
      placeholder: '',
      x: Math.round(pos.x),
      y: Math.round(pos.y),
      width: draggedFieldType === 'checkbox' ? 20 : 150,
      height: draggedFieldType === 'signature' ? 30 : (draggedFieldType === 'checkbox' ? 20 : 22),
      page: currentPage,
      pageWidth: pdfDimensions.width,
      pageHeight: pdfDimensions.height,
      required: false,
      isVirtual: true,
      validation: {}
    };

    setFields([...fields, newField]);
    setSelectedField(newField);
    setDraggedFieldType(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  // Field movement - mouse down on field body (NOT on resize handles)
  const handleFieldMouseDown = (e, field) => {
    // Check if clicking on a resize handle - if so, don't start drag
    const target = e.target;
    if (target.classList.contains('resize-handle') || target.closest('.resize-handle')) {
      return; // Let the resize handler take over
    }
    
    e.preventDefault();
    e.stopPropagation();
    
    setSelectedField(field);
    setIsDragging(true);
    setIsResizing(false); // Make sure we're not resizing
    setDragStart(getMousePosition(e));
    setFieldStart({ x: field.x, y: field.y, width: field.width, height: field.height });
  };

  // Resize handle mouse down - CRITICAL: must stop propagation
  const handleResizeMouseDown = (e, field, handle) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('Resize started:', handle); // Debug log
    
    setSelectedField(field);
    setIsResizing(true);
    setIsDragging(false); // Make sure we're not dragging
    setResizeHandle(handle);
    setDragStart(getMousePosition(e));
    setFieldStart({ x: field.x, y: field.y, width: field.width, height: field.height });
  };

  // Handle mouse move for drag/resize
  const handleMouseMove = useCallback((e) => {
    if (!isDragging && !isResizing) return;
    if (!selectedField) return;

    const pos = getMousePosition(e);
    const deltaX = pos.x - dragStart.x;
    const deltaY = pos.y - dragStart.y;

    setFields(prevFields => prevFields.map(f => {
      if (f.fieldId !== selectedField.fieldId) return f;

      if (isDragging) {
        // Move the field
        return {
          ...f,
          x: Math.max(0, Math.min(fieldStart.x + deltaX, pdfDimensions.width - f.width)),
          y: Math.max(0, Math.min(fieldStart.y + deltaY, pdfDimensions.height - f.height))
        };
      }

      if (isResizing) {
        let newX = fieldStart.x;
        let newY = fieldStart.y;
        let newWidth = fieldStart.width;
        let newHeight = fieldStart.height;

        // Handle different resize handles
        switch (resizeHandle) {
          case 'se': // Bottom-right
            newWidth = Math.max(20, fieldStart.width + deltaX);
            newHeight = Math.max(15, fieldStart.height + deltaY);
            break;
          case 'sw': // Bottom-left
            newX = Math.max(0, fieldStart.x + deltaX);
            newWidth = Math.max(20, fieldStart.width - deltaX);
            newHeight = Math.max(15, fieldStart.height + deltaY);
            break;
          case 'ne': // Top-right
            newY = Math.max(0, fieldStart.y + deltaY);
            newWidth = Math.max(20, fieldStart.width + deltaX);
            newHeight = Math.max(15, fieldStart.height - deltaY);
            break;
          case 'nw': // Top-left
            newX = Math.max(0, fieldStart.x + deltaX);
            newY = Math.max(0, fieldStart.y + deltaY);
            newWidth = Math.max(20, fieldStart.width - deltaX);
            newHeight = Math.max(15, fieldStart.height - deltaY);
            break;
          case 'e': // Right
            newWidth = Math.max(20, fieldStart.width + deltaX);
            break;
          case 'w': // Left
            newX = Math.max(0, fieldStart.x + deltaX);
            newWidth = Math.max(20, fieldStart.width - deltaX);
            break;
          case 's': // Bottom
            newHeight = Math.max(15, fieldStart.height + deltaY);
            break;
          case 'n': // Top
            newY = Math.max(0, fieldStart.y + deltaY);
            newHeight = Math.max(15, fieldStart.height - deltaY);
            break;
        }

        return { ...f, x: newX, y: newY, width: newWidth, height: newHeight };
      }

      return f;
    }));
  }, [isDragging, isResizing, selectedField, dragStart, fieldStart, resizeHandle, pdfDimensions, getMousePosition]);

  // Handle mouse up
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeHandle(null);
  }, []);

  // Add/remove event listeners
  useEffect(() => {
    if (isDragging || isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp]);

  const handleFieldDoubleClick = (e, field) => {
    e.stopPropagation();
    setSelectedField(field);
    setShowFieldConfig(true);
  };

  const handleDeleteField = (fieldId) => {
    setFields(fields.filter(f => f.fieldId !== fieldId));
    if (selectedField?.fieldId === fieldId) {
      setSelectedField(null);
    }
  };

  const handleFieldConfigSave = (updatedField) => {
    setFields(fields.map(f => f.fieldId === updatedField.fieldId ? updatedField : f));
    setSelectedField(updatedField);
    setShowFieldConfig(false);
  };

  const handleSaveMapping = async () => {
    if (fields.length === 0) {
      alert('Please add at least one field before saving.');
      return;
    }
    
    setSaving(true);
    try {
      await axiosInstance.put(`/admin/custom-forms/${formId}/mapping`, fields);
      alert('Field mapping saved successfully!');
      // Use replace to prevent back button returning to this page
      navigate('/admin/custom-forms', { replace: true });
    } catch (error) {
      console.error('Error saving mapping:', error);
      alert('Error saving mapping: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleAiDetect = async () => {
    if (!window.confirm('Use AI to automatically detect form fields?\n\nThis will replace existing fields with AI-detected ones.')) {
      return;
    }
    
    setAiDetecting(true);
    try {
      const response = await axiosInstance.post(`/admin/custom-forms/${formId}/ai-detect`);
      if (response.data.success) {
        setFields(response.data.fields || []);
        alert(`✓ AI detected ${response.data.fields?.length || 0} fields!\n\nDrag fields to adjust positions, use corner handles to resize.`);
      } else {
        alert(response.data.message || 'AI detection did not find any fields.');
      }
    } catch (error) {
      console.error('Error with AI detection:', error);
      alert('AI detection failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAiDetecting(false);
    }
  };

  const handleClearAll = () => {
    if (window.confirm('Remove all fields from this page?')) {
      setFields(fields.filter(f => f.page !== currentPage));
      setSelectedField(null);
    }
  };

  const token = localStorage.getItem('token');
  const pdfUrl = `${API}/custom-forms/${formId}/pdf?token=${encodeURIComponent(token || '')}`;

  const pageFields = fields.filter(f => f.page === currentPage);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-600">Loading form...</div>
      </div>
    );
  }

  // Show error state if form failed to load
  if (!form) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-lg text-center max-w-md">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Form Not Found</h2>
          <p className="text-gray-600 mb-6">{pdfError || 'Unable to load form. It may have been deleted.'}</p>
          <button
            onClick={() => navigate('/admin/custom-forms', { replace: true })}
            className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
          >
            Back to Forms
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4 sticky top-0 z-20">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/custom-forms', { replace: true })}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{form?.formName || 'Field Mapper'}</h1>
              <p className="text-gray-600">Drag fields to position • Use corner handles to resize</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleAiDetect}
              disabled={aiDetecting}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              {aiDetecting ? '🤖 Detecting...' : '🤖 AI Auto-Detect'}
            </button>
            <button
              onClick={() => navigate('/admin/custom-forms', { replace: true })}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveMapping}
              disabled={saving || fields.length === 0}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : `Save (${fields.length} fields)`}
            </button>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Left Sidebar - Field Palette */}
        <div className="w-64 bg-white border-r p-4 sticky top-20 h-[calc(100vh-5rem)] overflow-y-auto">
          <h3 className="font-semibold text-gray-900 mb-3">Field Types</h3>
          <p className="text-xs text-gray-500 mb-3">Drag onto the PDF</p>
          
          <div className="space-y-2">
            {fieldTypes.map(ft => (
              <div
                key={ft.type}
                draggable
                onDragStart={(e) => handlePaletteDragStart(e, ft.type)}
                className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200 cursor-grab hover:bg-gray-100 hover:border-orange-300 transition"
              >
                <span className="text-xl">{ft.icon}</span>
                <span className="text-sm font-medium">{ft.label}</span>
              </div>
            ))}
          </div>

          <hr className="my-4" />

          {/* Selected Field Info */}
          {selectedField && (
            <div className="bg-blue-50 rounded-lg p-3 mb-4">
              <h4 className="font-semibold text-sm text-blue-900 mb-2">Selected Field</h4>
              <div className="text-xs text-blue-800 space-y-1">
                <p><strong>Label:</strong> {selectedField.label}</p>
                <p><strong>Type:</strong> {selectedField.fieldType}</p>
                <p><strong>Position:</strong> {Math.round(selectedField.x)}, {Math.round(selectedField.y)}</p>
                <p><strong>Size:</strong> {Math.round(selectedField.width)} × {Math.round(selectedField.height)}</p>
              </div>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => setShowFieldConfig(true)}
                  className="flex-1 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDeleteField(selectedField.fieldId)}
                  className="flex-1 px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          )}

          {/* Page Fields List */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-semibold text-sm text-gray-700">Page {currentPage} Fields ({pageFields.length})</h4>
              {pageFields.length > 0 && (
                <button onClick={handleClearAll} className="text-xs text-red-600 hover:text-red-800">Clear All</button>
              )}
            </div>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {pageFields.map(f => (
                <div
                  key={f.fieldId}
                  onClick={() => setSelectedField(f)}
                  className={`p-2 text-xs rounded cursor-pointer truncate ${
                    selectedField?.fieldId === f.fieldId
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  {f.label}
                </div>
              ))}
              {pageFields.length === 0 && (
                <p className="text-xs text-gray-400 italic">No fields on this page</p>
              )}
            </div>
          </div>
        </div>

        {/* Main Content - PDF Viewer */}
        <div className="flex-1 p-6">
          <div className="flex justify-center">
            <div className="bg-white rounded-lg shadow-lg p-4">
              {/* PDF Controls */}
              <div className="flex items-center justify-between mb-4 pb-4 border-b">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50"
                  >
                    ◀
                  </button>
                  <span className="text-sm">Page {currentPage} of {numPages || '?'}</span>
                  <button
                    onClick={() => setCurrentPage(Math.min(numPages || 1, currentPage + 1))}
                    disabled={currentPage === numPages}
                    className="px-3 py-1 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50"
                  >
                    ▶
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setScale(Math.max(0.5, scale - 0.1))}
                    className="px-3 py-1 bg-gray-100 rounded hover:bg-gray-200"
                  >
                    −
                  </button>
                  <span className="text-sm w-16 text-center">{Math.round(scale * 100)}%</span>
                  <button
                    onClick={() => setScale(Math.min(2, scale + 0.1))}
                    className="px-3 py-1 bg-gray-100 rounded hover:bg-gray-200"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* PDF with Field Overlays */}
              {pdfError ? (
                <div className="text-red-600 p-8 text-center">
                  <p>{pdfError}</p>
                  <button
                    onClick={() => {
                      setPdfRetryCount(0);
                      setPdfKey(prev => prev + 1);
                      setPdfError(null);
                    }}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Retry Loading PDF
                  </button>
                </div>
              ) : (
                <div
                  ref={pdfContainerRef}
                  className="relative inline-block"
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onClick={() => setSelectedField(null)}
                  style={{ cursor: isDragging ? 'grabbing' : 'default' }}
                >
                  <Document
                    key={pdfKey}
                    file={pdfUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={onDocumentLoadError}
                    loading={<div className="flex items-center justify-center h-96 w-[612px] bg-white">Loading PDF...{pdfRetryCount > 0 ? ` (Attempt ${pdfRetryCount + 1})` : ''}</div>}
                  >
                    <Page
                      pageNumber={currentPage}
                      scale={scale}
                      onLoadSuccess={onPageLoadSuccess}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  </Document>

                  {/* Field Overlays with Resize Handles */}
                  {pageFields.map((field) => {
                    const isSelected = selectedField?.fieldId === field.fieldId;
                    return (
                      <div
                        key={field.fieldId}
                        className={`absolute border-2 transition-colors ${
                          isSelected
                            ? 'border-blue-500 bg-blue-200 bg-opacity-50 z-10'
                            : 'border-orange-500 bg-orange-200 bg-opacity-40 hover:bg-opacity-60'
                        }`}
                        style={{
                          left: `${field.x * scale}px`,
                          top: `${field.y * scale}px`,
                          width: `${field.width * scale}px`,
                          height: `${field.height * scale}px`,
                          cursor: isDragging && isSelected ? 'grabbing' : 'grab',
                        }}
                        onMouseDown={(e) => handleFieldMouseDown(e, field)}
                        onDoubleClick={(e) => handleFieldDoubleClick(e, field)}
                      >
                        {/* Field Label */}
                        <div
                          className="text-xs font-bold px-1 truncate pointer-events-none"
                          style={{
                            color: isSelected ? '#1d4ed8' : '#c2410c',
                            fontSize: `${Math.max(9, 11 * scale)}px`
                          }}
                        >
                          {field.label}
                          {field.required && <span className="text-red-500 ml-1">*</span>}
                        </div>

                        {/* Resize Handles - Only show when selected */}
                        {isSelected && (
                          <>
                            {/* Corner handles - LARGE for easy grabbing */}
                            <div
                              className="resize-handle absolute bg-blue-600 border-2 border-white rounded shadow-lg cursor-nw-resize z-20 hover:bg-blue-700 hover:scale-110 transition-transform"
                              style={{ top: -8, left: -8, width: 16, height: 16 }}
                              onMouseDown={(e) => handleResizeMouseDown(e, field, 'nw')}
                              title="Drag to resize"
                            />
                            <div
                              className="resize-handle absolute bg-blue-600 border-2 border-white rounded shadow-lg cursor-ne-resize z-20 hover:bg-blue-700 hover:scale-110 transition-transform"
                              style={{ top: -8, right: -8, width: 16, height: 16 }}
                              onMouseDown={(e) => handleResizeMouseDown(e, field, 'ne')}
                              title="Drag to resize"
                            />
                            <div
                              className="resize-handle absolute bg-blue-600 border-2 border-white rounded shadow-lg cursor-sw-resize z-20 hover:bg-blue-700 hover:scale-110 transition-transform"
                              style={{ bottom: -8, left: -8, width: 16, height: 16 }}
                              onMouseDown={(e) => handleResizeMouseDown(e, field, 'sw')}
                              title="Drag to resize"
                            />
                            <div
                              className="resize-handle absolute bg-green-500 border-2 border-white rounded shadow-lg cursor-se-resize z-20 hover:bg-green-600 hover:scale-110 transition-transform"
                              style={{ bottom: -8, right: -8, width: 20, height: 20 }}
                              onMouseDown={(e) => handleResizeMouseDown(e, field, 'se')}
                              title="⤡ Drag to resize (main handle)"
                            />
                            {/* Edge handles */}
                            <div
                              className="resize-handle absolute bg-blue-400 border-2 border-white rounded shadow cursor-e-resize z-20 hover:bg-blue-500"
                              style={{ top: '50%', right: -6, width: 12, height: 20, transform: 'translateY(-50%)' }}
                              onMouseDown={(e) => handleResizeMouseDown(e, field, 'e')}
                              title="Drag to resize width"
                            />
                            <div
                              className="resize-handle absolute bg-blue-400 border-2 border-white rounded shadow cursor-w-resize z-20 hover:bg-blue-500"
                              style={{ top: '50%', left: -6, width: 12, height: 20, transform: 'translateY(-50%)' }}
                              onMouseDown={(e) => handleResizeMouseDown(e, field, 'w')}
                              title="Drag to resize width"
                            />
                            <div
                              className="resize-handle absolute bg-blue-400 border-2 border-white rounded shadow cursor-s-resize z-20 hover:bg-blue-500"
                              style={{ bottom: -6, left: '50%', width: 20, height: 12, transform: 'translateX(-50%)' }}
                              onMouseDown={(e) => handleResizeMouseDown(e, field, 's')}
                              title="Drag to resize height"
                            />
                            <div
                              className="resize-handle absolute bg-blue-400 border-2 border-white rounded shadow cursor-n-resize z-20 hover:bg-blue-500"
                              style={{ top: -6, left: '50%', width: 20, height: 12, transform: 'translateX(-50%)' }}
                              onMouseDown={(e) => handleResizeMouseDown(e, field, 'n')}
                              title="Drag to resize height"
                            />
                            
                            {/* Size indicator */}
                            <div
                              className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded shadow whitespace-nowrap z-30"
                            >
                              {Math.round(field.width)} × {Math.round(field.height)} px
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Field Configuration Modal */}
      {showFieldConfig && selectedField && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-4">Configure Field</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Label</label>
                <input
                  type="text"
                  value={selectedField.label}
                  onChange={(e) => setSelectedField({ ...selectedField, label: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Field Type</label>
                <select
                  value={selectedField.fieldType}
                  onChange={(e) => setSelectedField({ ...selectedField, fieldType: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {fieldTypes.map(ft => (
                    <option key={ft.type} value={ft.type}>{ft.icon} {ft.label}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">X Position</label>
                  <input
                    type="number"
                    value={Math.round(selectedField.x)}
                    onChange={(e) => setSelectedField({ ...selectedField, x: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Y Position</label>
                  <input
                    type="number"
                    value={Math.round(selectedField.y)}
                    onChange={(e) => setSelectedField({ ...selectedField, y: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Width</label>
                  <input
                    type="number"
                    value={Math.round(selectedField.width)}
                    onChange={(e) => setSelectedField({ ...selectedField, width: parseFloat(e.target.value) || 20 })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Height</label>
                  <input
                    type="number"
                    value={Math.round(selectedField.height)}
                    onChange={(e) => setSelectedField({ ...selectedField, height: parseFloat(e.target.value) || 15 })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="required"
                  checked={selectedField.required || false}
                  onChange={(e) => setSelectedField({ ...selectedField, required: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="required" className="text-sm">Required field</label>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowFieldConfig(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleFieldConfigSave(selectedField)}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFormMapper;
